/**
 * Toast Utility
 *
 * Drop-in Replacement für react-hot-toast
 * Verwendet intern RFToast für natives Tailwind-Styling
 *
 * Usage: import toast from '../utils/toast';
 *        toast.success('Erfolgreich gespeichert!');
 */

import React from 'react';
import { rfToast } from '../components/ui/TWToast';

// Options-Interface kompatibel zu react-hot-toast
interface ToastOptions {
  id?: string;
  description?: string;
  duration?: number;
  icon?: React.ReactNode; // Ignoriert, aber für API-Kompatibilität behalten
  style?: React.CSSProperties; // Ignoriert, aber für API-Kompatibilität behalten
}

// Kompatible API zu react-hot-toast
const toast = Object.assign(
  // Default: info toast
  (message: string, options?: ToastOptions) => {
    if (options?.id) {
      rfToast.update(options.id, { type: 'info', title: message });
      return options.id;
    }
    return rfToast.info(message, options?.description, options?.duration);
  },
  {
    success: (message: string, options?: ToastOptions) => {
      if (options?.id) {
        rfToast.update(options.id, { type: 'success', title: message, duration: options?.duration || 4000 });
        return options.id;
      }
      return rfToast.success(message, options?.description, options?.duration);
    },

    error: (message: string, options?: ToastOptions) => {
      if (options?.id) {
        rfToast.update(options.id, { type: 'error', title: message, duration: options?.duration || 5000 });
        return options.id;
      }
      return rfToast.error(message, options?.description, options?.duration);
    },

    warning: (message: string, options?: ToastOptions) => {
      if (options?.id) {
        rfToast.update(options.id, { type: 'warning', title: message, duration: options?.duration || 4000 });
        return options.id;
      }
      return rfToast.warning(message, options?.description, options?.duration);
    },

    info: (message: string, options?: ToastOptions) => {
      if (options?.id) {
        rfToast.update(options.id, { type: 'info', title: message, duration: options?.duration || 4000 });
        return options.id;
      }
      return rfToast.info(message, options?.description, options?.duration);
    },

    loading: (message: string, options?: ToastOptions) => {
      if (options?.id) {
        rfToast.update(options.id, { type: 'loading', title: message, duration: 0 });
        return options.id;
      }
      return rfToast.loading(message);
    },

    dismiss: (id?: string) => {
      if (id) rfToast.dismiss(id);
    },

    promise: rfToast.promise,

    // Custom toast für spezielle Fälle
    custom: (message: string, options?: ToastOptions & { type?: 'success' | 'error' | 'warning' | 'info' }) => {
      const type = options?.type || 'info';
      switch (type) {
        case 'success': return rfToast.success(message, options?.description, options?.duration);
        case 'error': return rfToast.error(message, options?.description, options?.duration);
        case 'warning': return rfToast.warning(message, options?.description, options?.duration);
        default: return rfToast.info(message, options?.description, options?.duration);
      }
    }
  }
);

export default toast;
